/**
 * FirmaAtamaTalepPanel.jsx — "Firma Eşleştir" paneli (denetmen tarafı)
 *
 * Bir standın firma atama taleplerini listeler ve denetmenin SERBEST METNİ gerçek
 * bir firmayla eşleştirmesini sağlar. Stand Düzenle popup'ının içinde yaşıyor —
 * talepler ancak stand tarafı bir istek gönderdikten sonra doğduğu için, yeni stand
 * oluşturma anında gösterilecek bir şey yok (bkz. StandListPage.jsx CreateStandModal:
 * orada firma DOĞRUDAN atanır, talep akışı yoktur).
 *
 * Kaynak: docs/guides/FE-stand-firma-atama-talebi-guide.md
 *
 * ⚠️ Eşleştirme = onay. Talep, denetmen firmayı seçtiği anda kapanır ve
 * firma_to_stand satırı O AN yazılır. relation_level gönderilmez, sunucuda
 * zincirden yeniden hesaplanır — panelde gösterilen seviye bilgilendirmedir.
 * ⚠️ 409: standda zaten BAŞKA bir seviye-1 dekoratör var. Backend mevcut firmayı
 * mesajında adıyla söyler; çözüm önce eskisini standdan çıkarmak.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Card, CardBody, Badge, Button, Input, Spinner, Alert, Row, Col } from "reactstrap";
import {
  Link2, Check, X, RefreshCw, Clock, AlertTriangle, Mail, Phone, User, Inbox,
} from "lucide-react";
import FirmaSearchSelect from "./FirmaSearchSelect";
import {
  getAtamaTalepleri, ataAtamaTalebi, reddetAtamaTalebi,
  ATAMA_TALEP_DURUM, RELATION_LEVEL_LABELS, RED_NEDENI_MAX,
} from "../../services/firmaAtamaTalepService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

function TalepMeta({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <span className="text-muted small me-3">
      <Icon size={11} className="me-1" />{children}
    </span>
  );
}

export default function FirmaAtamaTalepPanel({ standId, fuarId, forceLive = false, onChanged }) {
  const { t: tx } = useTranslation("components");
  const [talepler, setTalepler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  // talepId → { firma, redMode, redNedeni, busy, error }
  const [draft, setDraft] = useState({});

  // KURAL: fetch mantığı Effect içinde tanımlanır, üst seviye bir fonksiyon olarak
  // paylaşılmaz — react-hooks/set-state-in-effect ihlalini önler (bkz.
  // StandListPage.jsx'teki aynı not). Yenileme reloadToken üzerinden tetiklenir.
  useEffect(() => {
    if (!standId || !fuarId) return undefined;
    let ignore = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const rows = await getAtamaTalepleri({ fuarId, standId }, { forceLive });
        if (!ignore) setTalepler(rows || []);
      } catch (e) {
        if (!ignore) setError(e?.message || i18n.t("components:firmaAtamaTalepPanel.taleplerAlinamadi"));
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [standId, fuarId, forceLive, reloadToken]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  function patchDraft(id, patch) {
    setDraft((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));
  }

  async function handleAta(talep) {
    const d = draft[talep.id] || {};
    if (!d.firma) return;
    patchDraft(talep.id, { busy: true, error: null });
    try {
      const updated = await ataAtamaTalebi(talep.id, d.firma.firmaId, { forceLive });
      // Canlı yanıt `atananFirmaAdi`'yi dolduruyor; taşımadığı durumda (mock, ya da
      // ileride adı atlayan bir sürüm) az önce SEÇİLEN firmanın adına düşüyoruz —
      // "Firma #47431" göstermek, karşısında adı bilinen bir seçim varken anlamsız.
      const merged = { ...updated, atananFirmaAdi: updated?.atananFirmaAdi || d.firma.firmaName };
      setTalepler((list) => list.map((t) => (t.id === talep.id ? { ...t, ...merged } : t)));
      patchDraft(talep.id, { busy: false, firma: null, redMode: false });
      onChanged?.();
    } catch (e) {
      patchDraft(talep.id, {
        busy: false,
        // 409'un ARTIK İKİ SEBEBİ VAR (2026-09-03): eski "standda zaten seviye-1
        // dekoratör var" ve yeni "bu dekoratör+taşeron+fuar üçlüsü için zaten aktif bir
        // firma_taseron bağlantısı var". İkincisinde çözüm standdan bir firma çıkarmak
        // DEĞİL, Taşeron Bağlantıları ekranından eski bağlantıyı sonlandırmak —
        // reddedilmiş bir bağlantı da aktif sayıldığı için kuyruğu kilitleyebiliyor.
        error: e?.status === 409
          ? `${e.message} ${/taşeron bağlantısı/i.test(String(e.message))
              ? tx("firmaAtamaTalepPanel.taseronBaglantilariEkranindanEskiBaglantiyi")
              : tx("firmaAtamaTalepPanel.standdaZatenBirSeviye1")}`
          : e?.message || tx("firmaAtamaTalepPanel.eslestirmeBasarisiz"),
      });
    }
  }

  async function handleReddet(talep) {
    const d = draft[talep.id] || {};
    const reason = String(d.redNedeni || "").trim();
    if (!reason) return;
    patchDraft(talep.id, { busy: true, error: null });
    try {
      const updated = await reddetAtamaTalebi(talep.id, reason, { forceLive });
      setTalepler((list) => list.map((t) => (t.id === talep.id ? { ...t, ...updated } : t)));
      patchDraft(talep.id, { busy: false, redMode: false, redNedeni: "" });
      onChanged?.();
    } catch (e) {
      patchDraft(talep.id, { busy: false, error: e?.message || tx("firmaAtamaTalepPanel.reddetmeBasarisiz") });
    }
  }

  const bekleyenSayisi = talepler.filter((t) => Number(t.isProcessed) === 0).length;

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <span className="fw-semibold">
          <Link2 size={14} className="me-1" />{tx("firmaAtamaTalepPanel.firmaEslestirmeTalepleri")}
        </span>
        {bekleyenSayisi > 0 && <Badge color="warning" pill>{bekleyenSayisi}{" "}{tx("firmaAtamaTalepPanel.bekliyor")}</Badge>}
        <Button
          color="secondary" outline size="sm" className="ms-auto"
          onClick={reload} disabled={loading} title={tx("common:yenile")}
        >
          <RefreshCw size={13} />
        </Button>
      </div>

      {error && <Alert color="danger" className="py-2 small">{error}</Alert>}

      {loading && talepler.length === 0 && (
        <div className="text-center py-3"><Spinner size="sm" /></div>
      )}

      {!loading && !error && talepler.length === 0 && (
        <div className="text-muted small d-flex align-items-center gap-2 py-2">
          <Inbox size={14} />
          {tx("firmaAtamaTalepPanel.buStandIcinFirmaAtama")}
        </div>
      )}

      {talepler.map((t) => {
        const d = draft[t.id] || {};
        const bekliyor = Number(t.isProcessed) === 0;
        const durum = ATAMA_TALEP_DURUM[Number(t.isProcessed)] || ATAMA_TALEP_DURUM[0];

        return (
          <Card key={t.id} className="mb-2 border">
            <CardBody className="p-2">
              <div className="d-flex align-items-start gap-2">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {/* Talep edilen firma SERBEST METİN — bir id değil, bu yüzden
                        tırnak içinde ve olduğu gibi gösteriliyor. */}
                    <span className="fw-semibold">&ldquo;{t.firmaNameText}&rdquo;</span>
                    <Badge color="info" pill>{t.requestedFirmaTypeAdi || tx("firmaAtamaTalepPanel.tip", { requestedFirmaTypeId: t.requestedFirmaTypeId })}</Badge>
                    <Badge color="light" className="text-muted">
                      {RELATION_LEVEL_LABELS[t.relationLevel] || tx("firmaAtamaTalepPanel.seviye", { relationLevel: t.relationLevel })}
                    </Badge>
                    <Badge color={durum.color} pill>{durum.label}</Badge>
                  </div>

                  <div className="mt-1">
                    <TalepMeta icon={User}>{t.firmaContactNameText}</TalepMeta>
                    <TalepMeta icon={Phone}>{t.firmaContactPhoneText}</TalepMeta>
                    <TalepMeta icon={Mail}>{t.firmaContactEmailText}</TalepMeta>
                  </div>

                  <div className="text-muted small mt-1">
                    <Clock size={11} className="me-1" />
                    {t.talepTarihi || "—"}{" "}{tx("firmaAtamaTalepPanel.talepEden")}{" "}{t.talepEdenFirmaAdi || `#${t.talepEdenFirmaId}`}
                  </div>

                  {t.maksimumDerinlikte && (
                    <div className="text-warning small mt-1">
                      <AlertTriangle size={11} className="me-1" />
                      {tx("firmaAtamaTalepPanel.zincirEnDerinSeviyedeBu")}
                    </div>
                  )}
                  {t.belgePenceresiKapali && (
                    <div className="text-muted small mt-1">
                      <AlertTriangle size={11} className="me-1" />
                      {tx("firmaAtamaTalepPanel.fuarinBelgeYuklemePenceresiKapali")}
                    </div>
                  )}
                  {/* ⚠️ 2026-09-03: onayın İKİ HEDEFİ var. Talep bir TAŞERON içinse (>=4)
                      ve talep eden bu standın dekoratörüyse (katılımcısı değil), onay
                      `firma_taseron`'a düşer: firma STANDA EKLENMEZ, dekoratör+fuara
                      bağlanır ve ayrıca fuara eklenir. Backend bunu bekleyen satırda
                      SÖYLEMİYOR (`dekoratoreBaglanacak` yalnızca create yanıtında var),
                      bu yüzden rolden çıkarım yapıp ihtimali yazıyoruz — kesin dil
                      kullanmıyoruz, çünkü çapa onay anında canlı zincirden hesaplanıyor. */}
                  {Number(t.requestedFirmaTypeId) >= 4 && Number(t.isProcessed) === 0 && (
                    <div className="text-info small mt-1">
                      <AlertTriangle size={11} className="me-1" />
                      {tx("firmaAtamaTalepPanel.taseronTalebiTalepEdenBu")}{" "}<strong>{tx("firmaAtamaTalepPanel.dekoratoru")}</strong>{" "}{tx("firmaAtamaTalepPanel.iseOnayStandaDegil")}{" "}<strong>{tx("firmaAtamaTalepPanel.dekoratorFuar")}</strong>{" "}{tx("firmaAtamaTalepPanel.baglantisinaTaseronBaglantilariDuserFirma")}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Karar alanı ────────────────────────────────────────────────── */}
              {bekliyor && (
                <div className="mt-2 pt-2 border-top">
                  {d.error && <Alert color="danger" className="py-1 px-2 small mb-2">{d.error}</Alert>}

                  {!d.redMode ? (
                    <Row className="g-2 align-items-end">
                      <Col md={7}>
                        <FirmaSearchSelect
                          label={tx("firmaAtamaTalepPanel.buMetninIsaretEttigiGercek")}
                          value={d.firma || null}
                          onChange={(firma) => patchDraft(t.id, { firma })}
                          forceLive={forceLive}
                          disabled={d.busy}
                          placeholder={tx("firmaAtamaTalepPanel.firmaAraVeEslestir")}
                        />
                      </Col>
                      <Col md={5} className="d-flex gap-2">
                        <Button
                          color="success" size="sm" className="flex-grow-1"
                          disabled={!d.firma || d.busy}
                          onClick={() => handleAta(t)}
                        >
                          {d.busy ? <Spinner size="sm" className="me-1" /> : <Check size={14} className="me-1" />}
                          {tx("firmaAtamaTalepPanel.eslestirVeOnayla")}
                        </Button>
                        <Button
                          color="danger" outline size="sm"
                          disabled={d.busy}
                          onClick={() => patchDraft(t.id, { redMode: true })}
                        >
                          <X size={14} className="me-1" />{tx("firmaAtamaTalepPanel.reddet")}
                        </Button>
                      </Col>
                    </Row>
                  ) : (
                    <div>
                      <Input
                        type="textarea" bsSize="sm" rows={2}
                        maxLength={RED_NEDENI_MAX}
                        value={d.redNedeni || ""}
                        disabled={d.busy}
                        placeholder={tx("firmaAtamaTalepPanel.redGerekcesiZorunluTalepEden")}
                        onChange={(e) => patchDraft(t.id, { redNedeni: e.target.value })}
                      />
                      <div className="d-flex gap-2 mt-2">
                        <Button
                          color="danger" size="sm"
                          disabled={!String(d.redNedeni || "").trim() || d.busy}
                          onClick={() => handleReddet(t)}
                        >
                          {d.busy ? <Spinner size="sm" className="me-1" /> : <X size={14} className="me-1" />}
                          {tx("firmaAtamaTalepPanel.talebiReddet")}
                        </Button>
                        <Button
                          color="secondary" outline size="sm" disabled={d.busy}
                          onClick={() => patchDraft(t.id, { redMode: false, redNedeni: "" })}
                        >
                          {tx("firmaAtamaTalepPanel.vazgec")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!bekliyor && (
                <div className="mt-2 pt-2 border-top small">
                  {Number(t.isProcessed) === 1 ? (
                    <span className="text-success">
                      <Check size={12} className="me-1" />
                      {t.atananFirmaAdi || tx("firmaAtamaTalepPanel.firma", { atananFirmaId: t.atananFirmaId })}{" "}{tx("firmaAtamaTalepPanel.olarakEslestirildi")}
                      {t.islemTarihi ? ` · ${t.islemTarihi}` : ""}
                    </span>
                  ) : (
                    <span className="text-danger">
                      <X size={12} className="me-1" />{tx("firmaAtamaTalepPanel.reddedildi")}{" "}{t.redNedeni || "—"}
                    </span>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}
